import { NiceChannelBox } from '@/features/analytics/components/nice-channel-box'
import { StatusBadge } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/cells/StatusCell/StatusBadge'
import { CreativeBadge } from '@/features/creative/creative-select/CreativeBadge'
import { ICreative, useGetLinkedLinksByContract } from '@/shared/api'
import { DocumentIcon } from '@/shared/assets'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    RubleIconInline,
} from '@/shared/ui'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import { WorkspaceDescriptor } from '@synapex/shared2/entities'
import { ActUnion, ContractUnion } from '@synapex/shared2/gateway-rest-api'
import { parseTelegramPeerReference } from '@synapex/shared2/telegram'
import clsx from 'clsx'
import React from 'react'

type LinkedLinksProps = {
    open: boolean
    setOpen: (open: boolean) => void
    draftContract: ContractUnion | undefined
    draftAct: ActUnion | undefined
    markingFormData: ORDMarkingFormData
} & WorkspaceDescriptor

const LinkedLinks: React.FC<LinkedLinksProps> = ({
    open,
    setOpen,
    draftContract,
    draftAct,
    markingFormData,
    ...props
}) => {
    const contractId =
        draftContract?.type === 'default'
            ? markingFormData.draft_contract_id
            : markingFormData.custom_contract_id

    const { data: linkedLinks } = useGetLinkedLinksByContract(
        props,
        contractId!,
        markingFormData.custom_contract_id ? 'custom' : 'draft',
        {
            enabled: !!contractId && !!draftContract && open,
        }
    )

    const sum =
        linkedLinks?.links.reduce((acc, link) => link.budget + acc, 0) ?? 0

    const actDisplayName =
        draftAct?.type === 'custom'
            ? `Акт №${draftAct.number}`
            : draftAct?.type === 'default'
              ? draftAct.fileName
              : ''

    return (
        <Dialog open={open} onOpenChange={setOpen} modal>
            <DialogContent
                aria-describedby={undefined}
                className="left-auto right-16 top-1/3 w-[505px] translate-x-0 translate-y-0 gap-0 overflow-y-auto bg-white p-4 pt-6 shadow-[0px_4px_24px_0px_rgba(0,0,0,0.15)]"
            >
                <DialogHeader className="w-full justify-center pb-4 text-xs font-black">
                    <DialogTitle className="flex w-full items-center">
                        <div className="flex items-center gap-3">
                            <span>Связанные элементы</span>
                        </div>
                    </DialogTitle>
                </DialogHeader>

                <div className="pb-4">
                    <div className="flex items-center gap-2">
                        <div className="relative flex w-[141px] flex-col gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Договор
                            </span>
                            <div>
                                <div className="flex items-center gap-1 rounded-[5px] border px-[10px] py-2 text-xs">
                                    {draftContract?.type === 'custom' ? (
                                        <>
                                            <img
                                                src={DocumentIcon}
                                                className="h-4 w-3 shrink-0"
                                            />
                                            <span className="truncate">
                                                Договор №{draftContract.number}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <img
                                                src={DocumentIcon}
                                                className="h-4 w-3 shrink-0"
                                            />
                                            <span className="truncate">
                                                {draftContract?.type ===
                                                'default'
                                                    ? draftContract.fileName
                                                    : ''}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex w-[141px] flex-col gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Акт
                            </span>
                            <div className="flex gap-1 rounded-[5px] border px-[10px] py-2 text-xs">
                                <img src={DocumentIcon} className="h-4 w-3" />
                                {actDisplayName || '—'}
                            </div>
                        </div>

                        <div className="flex w-[159px] flex-col gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Стоимость итого
                            </span>
                            <div className="flex items-center gap-1 rounded-[5px] border px-[10px] py-2 text-xs">
                                {sum} <RubleIconInline className="mb-0" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 max-h-[300px] overflow-y-auto overflow-x-hidden">
                    <table className="w-full">
                        <thead className="sticky top-0 z-10 bg-white">
                            <tr className="flex gap-2 text-[10px] text-black/40">
                                <th className="w-[141px] text-start">
                                    Площадка
                                </th>
                                <th className="w-[141px] text-start">
                                    Креатив
                                </th>
                                <th className="w-[53px] text-start">Цена</th>
                                <th className="w-[98px] text-nowrap text-start">
                                    Статус публикации
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {linkedLinks?.links.map((row) => {
                                let key
                                let linkName
                                if (row.workspace_type === 'site') {
                                    key = row.site_link_id
                                    linkName = row.site_link_name
                                } else {
                                    key = row.invite_link_id
                                    linkName = row.invite_link_name
                                }
                                return (
                                    <tr
                                        key={key}
                                        className="flex items-center gap-2 py-1"
                                    >
                                        <td className="w-[141px]">
                                            <NiceChannelBox
                                                {...parseTelegramPeerReference(
                                                    linkName
                                                )}
                                                title={
                                                    row.external_channel
                                                        ?.telegram_channel_name
                                                }
                                                toastClassName="toast__marking-modal"
                                                className={clsx(
                                                    'name-cell__channel-box',
                                                    {
                                                        new:
                                                            new Date().getTime() -
                                                                new Date(
                                                                    row.created_at
                                                                ).getTime() <=
                                                            1 * 10 * 1000,
                                                    },
                                                    'w-full'
                                                )}
                                                photoUrl={
                                                    row.external_channel
                                                        ?.small_photo_data_url
                                                }
                                                rknStatus={
                                                    row.external_channel
                                                        ?.rkn_status ??
                                                    undefined
                                                }
                                            />
                                        </td>
                                        <td className="w-[141px]">
                                            {row.active_creative ? (
                                                <CreativeBadge
                                                    data={
                                                        row.active_creative as unknown as ICreative
                                                    }
                                                />
                                            ) : null}
                                        </td>
                                        <td className="w-[53px] text-xs">
                                            {row.budget} ₽
                                        </td>
                                        <td className="w-[98px]">
                                            <StatusBadge
                                                className={
                                                    'min-w-0 max-w-[100px] grow'
                                                }
                                                value={row.status}
                                            />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default LinkedLinks
